/* eslint-disable max-lines */
import React from 'react';
import cn from 'clsx';
import { XIcon } from '@fronton/icons-react';
import { PromoModalStructure } from 'modules/model-json';
import { createElementKey } from 'utils';
import { strictBreakpoints } from 'common/styles/breakpoints';
import { Button, ButtonWithGAClickButton } from '../button';
import { Modal } from '../modal';
import { SectionSubTitle } from '../section-subtitle';
import { Typography } from '../typography';
import {
  promoModal,
  promoModalBody,
  promoModalBodyWrapper,
  promoModalButton,
  promoModalCloseButton,
  promoModalContent,
  promoModalDisclaimer,
  promoModalDisclaimerText,
  promoModalImage,
  promoModalImageWrapper,
  promoModalOptionList,
  promoModalOptionMarkerTypeMap,
  promoModalSubTitle,
  promoModalText,
  promoModalTitle,
} from './promo-modal-styles';

type PromoModalProps = {
  elementBlock: string;
  elementName: string;
  modalContent: PromoModalStructure;
  isOpen: boolean;
  onClose: () => void;
  onModalBtnClick?: () => void;
  regionId: string;
};

const makeUrlsClickable = (text: string) =>
  text.replace(/link/g, 'a').replace(/&lt;/g, '<').replace(/&gt;/g, '>');

export const PromoModal: React.FC<PromoModalProps> = ({
  elementBlock,
  elementName,
  modalContent,
  isOpen,
  onClose,
  onModalBtnClick,
  regionId,
}) => {
  // Вставляем код региона, если сслыка региональная
  const btnHref = modalContent.button?.link?.includes('regionId')
    ? modalContent.button?.link?.replace('regionId', regionId)
    : modalContent.button?.link;
  const handleButtonClick = () => {
    // Если ссылки нет, вызываем стандартный экшен нажатия кнопки
    if (!btnHref && onModalBtnClick) {
      onModalBtnClick();
    }
    onClose();
  };

  return (
    <Modal
      className={promoModal}
      closeButton={false}
      onClose={onClose}
      show={isOpen}
      size="l"
      variant="curtain">
      <Button
        adaptive={false}
        size="s"
        className={promoModalCloseButton}
        iconOnly
        variant="pseudo"
        label="Close"
        rounded
        onClick={onClose}>
        <XIcon type="outline" size="l" color="text-invert" />
      </Button>

      <div className={promoModalContent}>
        <div className={promoModalImageWrapper}>
          <picture>
            <source
              srcSet={modalContent.media.bannerLargeRef}
              media={strictBreakpoints['--only-largeDesktop']}
            />
            <source
              srcSet={modalContent.media.bannerDesktopRef}
              media={strictBreakpoints['--only-desktop']}
            />
            <source srcSet={modalContent.media.bannerTabletRef} media={strictBreakpoints['--only-tablet']} />
            <img
              alt={modalContent.media.creative}
              className={promoModalImage}
              src={modalContent.media.bannerMobileRef}
            />
          </picture>
        </div>

        <div className={promoModalBodyWrapper}>
          <div className={promoModalBody}>
            <SectionSubTitle adaptive margin="s" as="h2" variant="h1">
              {modalContent.modalTitle}
            </SectionSubTitle>
            {modalContent.title && (
              <Typography className={promoModalTitle} variant="h3">
                {modalContent.title}
              </Typography>
            )}
            {modalContent.subTitle && (
              <Typography className={promoModalSubTitle} variant="m" size="body_accent">
                {modalContent.subTitle}
              </Typography>
            )}

            {modalContent?.topText?.map(
              (text, index) =>
                text && (
                  <Typography
                    as="p"
                    className={cn(promoModalText, promoModalSubTitle)}
                    dangerouslySetInnerHTML={{ __html: makeUrlsClickable(text) }}
                    key={createElementKey('topText', index)}
                    size="body_long"
                    variant="m"
                  />
                )
            )}

            {modalContent.optionList.length > 0 && (
              <ul
                className={cn(
                  promoModalOptionList,
                  promoModalOptionMarkerTypeMap[modalContent.optionList[0].description]
                )}>
                {modalContent.optionList.map(
                  (option, index) =>
                    option.title && (
                      <li key={createElementKey('option', index)}>
                        <Typography
                          className={promoModalText}
                          dangerouslySetInnerHTML={{ __html: makeUrlsClickable(option.title) }}
                          size="body_long"
                          variant="m"
                        />
                      </li>
                    )
                )}
              </ul>
            )}

            {modalContent?.bottomText?.map(
                (text, index) =>
                  text && (
                    <Typography
                      as="p"
                      className={cn(promoModalText, promoModalSubTitle)}
                      dangerouslySetInnerHTML={{ __html: makeUrlsClickable(text) }}
                      key={createElementKey('bottomText', index)}
                      size="body_long"
                      variant="m"
                    />
                  )
              )}

            {modalContent.button?.label && (
              <div className={promoModalButton}>
                <ButtonWithGAClickButton
                  elementBlock={elementBlock}
                  elementName={elementName}
                  elementText={modalContent.button.label}
                  {...modalContent.button}
                  href={btnHref}
                  onClick={handleButtonClick}
                >
                  {modalContent.button.label}
                </ButtonWithGAClickButton>
              </div>
            )}

            {(modalContent.disclaimer && modalContent.disclaimer.length > 0) && (
              <div className={promoModalDisclaimer}>
                {modalContent.disclaimer.map(
                  (text, index) =>
                    text && (
                      <Typography
                        className={promoModalDisclaimerText}
                        color="text-minor"
                        dangerouslySetInnerHTML={{ __html: makeUrlsClickable(text) }}
                        key={createElementKey('disclaimer', index)}
                        variant="s"
                        size="body_short"
                      />
                    )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
